import { withGitpodSize } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useCanDeleteWebhook,
    useGetWebhookSecret,
    useRotateWebhookSecret,
    useUpdateWebhook,
    type PlainWebhook,
} from "@/queries/webhook-queries";
import { IconArrowRotateLeftRight } from "central-icons/IconArrowRotateLeftRight";
import { useState, useEffect, useMemo, type FC } from "react";
import { atom, useAtom } from "jotai";
import { atomEffect } from "jotai-effect";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { DeleteWebhookDialog } from "@/components/webhooks/DeleteWebhookDialog";
import { WebhookScopeField } from "@/components/webhooks/WebhookScopeField";
import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGitLab } from "@/assets/icons/geist/IconGitLab";
import { cn } from "@/components/podkit/lib/cn";

type WebhookDetailsDialogProps = {
    webhook?: PlainWebhook;
    open: boolean;
    onClose: () => void;
    isLoading?: boolean;
};

export const WebhookDetailsDialog: FC<WebhookDetailsDialogProps> = ({ open, onClose, webhook, isLoading }) => {
    const { toast } = useToast();
    const { data: secretToken, isLoading: isLoadingSecret } = useGetWebhookSecret(webhook?.id);
    const { mutateAsync: rotateSecret, isPending: isRotating } = useRotateWebhookSecret();
    const { mutateAsync: updateWebhook, isPending: isUpdatingWebhook } = useUpdateWebhook();
    const { canDeleteWebhook } = useCanDeleteWebhook(webhook?.id);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedScope, setSelectedScope] = useState(webhook?.spec?.scope || "");

    const webhookScope = webhook?.spec?.scope || "";
    const provider = webhook?.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const webhookType = webhook?.spec?.type ?? WebhookType.UNSPECIFIED;
    const payloadUrl = webhook?.url || "";

    // Scope is read-only if already set
    const hasExistingScope = !!webhookScope;

    // Sync local state when webhook changes
    useEffect(() => {
        setSelectedScope(webhookScope);
    }, [webhookScope]);

    const handleScopeChange = async (newScope: string) => {
        setSelectedScope(newScope);
        if (!webhook || newScope === webhookScope) return;
        try {
            await updateWebhook({
                webhookId: webhook.id,
                scope: newScope,
            });
        } catch (error) {
            toast({
                title: "Failed to update webhook scope",
                description: formatError(error),
            });
            // Revert to original scope on error
            setSelectedScope(webhookScope);
        }
    };

    const handleRotateSecret = async () => {
        if (!webhook) return;
        try {
            await rotateSecret(webhook.id);
            toast({
                title: "Webhook secret has been rotated",
            });
        } catch (error) {
            toast({
                title: "Failed to rotate webhook secret",
                description: formatError(error),
            });
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setShowDeleteDialog(false);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="webhook-details-dialog"
                data-track-location={TrackLocations.ConfigureWebhookDialog}
            >
                <DialogHeader className="mb-2">
                    <DialogTitle>{isLoading ? "Loading..." : "Webhook configuration"}</DialogTitle>
                    <DialogDescription>
                        {isLoading
                            ? "Loading webhook details"
                            : "Set up a webhook to connect Ona with your GitHub or GitLab repositories."}
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-4 px-0">
                    {isLoading ? (
                        <LoadingState />
                    ) : (
                        <div className="flex flex-col gap-4">
                            {/* Name field - editable with auto-save */}
                            {webhook && (
                                <WebhookNameField webhookId={webhook.id} initialName={webhook.metadata?.name || ""} />
                            )}

                            {/* Location field - read-only, same visual style as new webhook dialog */}
                            <LocationDisplay provider={provider} />

                            {/* Organization/Repository field */}
                            <WebhookScopeField
                                provider={provider}
                                webhookType={webhookType}
                                value={selectedScope}
                                onChange={handleScopeChange}
                                readOnly={hasExistingScope}
                                disabled={isUpdatingWebhook}
                            />

                            {/* Payload URL field */}
                            <div className="flex flex-col gap-2">
                                <Label className="text-sm font-medium" htmlFor="payload-url">
                                    Payload URL
                                </Label>
                                <CopyableInput
                                    id="payload-url"
                                    value={payloadUrl}
                                    data-testid="payload-url-input"
                                    data-tracking-id="copy-payload-url-webhook-details-dialog"
                                />
                                <span className="text-sm text-content-secondary">
                                    Use this endpoint to receive events from your SCM provider.
                                </span>
                            </div>

                            {/* Secret token field with rotation */}
                            <div className="flex flex-col gap-2">
                                <Label className="text-sm font-medium" htmlFor="secret-token">
                                    Secret token
                                </Label>
                                <div className="flex items-center gap-2">
                                    <CopyableInput
                                        id="secret-token"
                                        type="password"
                                        value={secretToken || ""}
                                        disabled={isLoadingSecret}
                                        data-testid="secret-token-input"
                                        data-tracking-id="copy-secret-token-webhook-details-dialog"
                                        wrapperClassName="grow"
                                    />
                                    <Button
                                        variant="outline"
                                        onClick={handleRotateSecret}
                                        disabled={isRotating || isLoadingSecret}
                                        loading={isRotating}
                                        data-testid="rotate-secret-button"
                                        data-tracking-id="rotate-secret-webhook-details-dialog"
                                        LeadingIcon={IconArrowRotateLeftRightWithSize}
                                    />
                                </div>
                                <span className="text-sm text-content-secondary">
                                    Use this token to verify webhook signatures.
                                </span>
                            </div>
                        </div>
                    )}
                </DialogBody>
                {!isLoading && (
                    <DialogFooter>
                        <Button
                            variant="destructive-secondary"
                            onClick={handleDeleteClick}
                            disabled={!canDeleteWebhook}
                            data-testid="delete-webhook-button"
                            data-tracking-id="delete-webhook-details-dialog"
                        >
                            Delete
                        </Button>
                        <DialogClose asChild>
                            <Button variant="outline" data-testid="done-webhook-button">
                                Done
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                )}
            </DialogContent>
            {webhook && (
                <DeleteWebhookDialog webhook={webhook} open={showDeleteDialog} onClose={handleDeleteDialogClose} />
            )}
        </Dialog>
    );
};

const IconArrowRotateLeftRightWithSize = withGitpodSize(IconArrowRotateLeftRight);

type LocationDisplayProps = {
    provider: WebhookProvider;
};

const LocationDisplay: FC<LocationDisplayProps> = ({ provider }) => {
    return (
        <div className="flex select-none flex-col">
            <span className="mb-2 text-sm font-medium text-content-primary">Location</span>
            <div className="flex gap-3">
                <div
                    className={cn(
                        "flex items-center gap-2 rounded-lg p-3",
                        provider === WebhookProvider.GITHUB
                            ? "border-[1.5px] border-border-brand bg-surface-brand-subtle shadow"
                            : "border border-border-base opacity-50",
                        provider !== WebhookProvider.GITHUB && "m-[0.5px]",
                    )}
                    data-testid="webhook-provider-github-display"
                >
                    <IconGitHub size="sm" className="text-content-primary" />
                    <span className="text-sm text-content-primary">GitHub</span>
                </div>
                <div
                    className={cn(
                        "flex items-center gap-2 rounded-lg p-3",
                        provider === WebhookProvider.GITLAB
                            ? "border-[1.5px] border-border-brand bg-surface-brand-subtle shadow"
                            : "border border-border-base opacity-50",
                        provider !== WebhookProvider.GITLAB && "m-[0.5px]",
                    )}
                    data-testid="webhook-provider-gitlab-display"
                >
                    <IconGitLab size="sm" className="text-content-primary" />
                    <span className="text-sm text-content-primary">GitLab</span>
                </div>
            </div>
        </div>
    );
};

type WebhookNameFieldProps = {
    webhookId: string;
    initialName: string;
};

const WebhookNameField: FC<WebhookNameFieldProps> = ({ webhookId, initialName }) => {
    const { toast } = useToast();
    const { mutateAsync: updateWebhook } = useUpdateWebhook();

    const atoms = useMemo(() => {
        const nameAtom = atom(initialName);
        const lastSavedNameAtom = atom(initialName);

        const autoSaveEffect = atomEffect((get, set) => {
            const currentName = get(nameAtom);
            const lastSavedName = get.peek(lastSavedNameAtom);

            // Skip if name hasn't changed from last saved value
            if (currentName === lastSavedName) {
                return;
            }

            // Debounce the save operation
            const timeoutId = setTimeout(async () => {
                try {
                    await updateWebhook({
                        webhookId,
                        name: currentName.trim() || undefined,
                    });
                    set(lastSavedNameAtom, currentName);
                } catch (error) {
                    toast({
                        title: "Failed to update webhook name",
                        description: formatError(error),
                    });
                    // Revert to last saved name on error
                    set(nameAtom, lastSavedName);
                }
            }, 500);

            return () => clearTimeout(timeoutId);
        });

        return { nameAtom, autoSaveEffect };
    }, [webhookId, updateWebhook, toast, initialName]);

    const [name, setName] = useAtom(atoms.nameAtom);
    useAtom(atoms.autoSaveEffect);

    return (
        <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium" htmlFor="webhook-name">
                Name
            </Label>
            <Input
                data-1p-ignore
                data-lpignore="true"
                data-protonpass-ignore="true"
                id="webhook-name"
                placeholder="My Webhook"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="webhook-name-input"
                maxLength={80}
            />
        </div>
    );
};
